import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import CommandPalette from '../Common/CommandPalette';
import { useUser } from '../../contexts/UserContext';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();

  // Close sidebar and reset scroll lock on route change
  useEffect(() => {
    setSidebarOpen(false);
    // Safety net: always clear body scroll lock on navigation
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.top = '';
  }, [location.pathname]);

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [sidebarOpen]);

  // Hotkey: Ctrl/Cmd+K
  useEffect(() => {
    const handler = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC')>=0;
      if ((isMac && e.metaKey && e.key.toLowerCase() === 'k') || (!isMac && e.ctrlKey && e.key.toLowerCase() === 'k')) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, logout } = useUser();

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
  };

  return (
    <div className="app-shell min-h-screen text-gray-900 dark:text-gray-100">
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Main Content - uses margin on desktop for sidebar space */}
      <div className="app-shell__main lg:ml-64 min-h-screen flex flex-col">
        {/* Topbar — single mount. Fixed positioning on mobile, static on desktop.
            Previously this was two wrappers (mobile + desktop) which mounted Topbar
            twice and made NotificationCenter run 4× in the React tree (combined with
            the mobile/desktop split inside Topbar itself). */}
        <div className="app-shell__topbar fixed top-0 left-0 right-0 z-50 lg:static">
          <Topbar
            onHamburger={() => setSidebarOpen(true)}
            onOpenCommandPalette={() => setPaletteOpen(true)}
          />
        </div>

        {/* Page content - add top padding on mobile to account for fixed header.
            pb-24 RESERVES THE FLOATING BUTTON'S CORNER. ReportProblemButton is mounted
            once in App.js as `fixed bottom-6 right-6 z-[9999] w-12 h-12`, so it owns a
            96px square at the bottom right of EVERY page and sits above everything. Any
            page whose content reaches the bottom of the viewport puts its own controls
            under it: measured on Smart Scheduling, the right ~23px of "Save Settings"
            was covered, and a click there opened the report dialog instead of saving.
            Reserving the space here fixes it for every page at once rather than nudging
            one Save button and leaving the next one to be found by the next audit.
            (Tailwind emits pb-* after p-*, so this wins over the p-4/lg:p-6 bottom.) */}
        <main className="app-shell__content flex-1 pt-16 lg:pt-0 p-4 lg:p-6 pb-24 w-full max-w-full overflow-x-hidden">
          {children}
        </main>

        <CommandPalette isOpen={paletteOpen} onClose={() => setPaletteOpen(false)} />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="app-shell__overlay fixed inset-0 z-40 lg:hidden tap-highlight-none"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* User menu overlay */}
      {userMenuOpen && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => setUserMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;

